import { ApplicationControlAction, JSONObject } from '../../types/types';
import { addBracketParameterToUrl } from '../../api/utils/addBracketParameterToUrl';
import { resolveStringPlaceholders } from '../json/resolveStringPlaceholders';
import { maskPlaceholders } from './maskPlaceholders';
import { unmaskPlaceholders } from './unmaskPlaceholders';
import { resolvePairSide } from './resolvePairSide';

// Builds the app url — link + params pairs resolved per side and url-encoded.
export const resolveAppActionUrl = async (
  action: ApplicationControlAction,
  templateData: JSONObject
): Promise<string> => {
  const resolvedLink = String(
    await resolveStringPlaceholders(action.link, templateData)
  );

  const paramsTemplate = (action.params ?? '').replace(/^[?&]+/, '');
  const { masked, placeholders } = maskPlaceholders(paramsTemplate);
  const resolveSide = (fragment: string) =>
    resolvePairSide(unmaskPlaceholders(fragment, placeholders), templateData);

  const resolvedPairs = await Promise.all(
    masked.split('&').map(async (pair) => {
      const equalsIndex = pair.indexOf('=');
      if (equalsIndex === -1) {
        // Flag parameter without a value.
        return resolveSide(pair);
      }
      const key = await resolveSide(pair.slice(0, equalsIndex));
      const value = await resolveSide(pair.slice(equalsIndex + 1));
      return `${key}=${value}`;
    })
  );

  // Filtered AFTER resolving — an empty pair must not leave a doubled separator.
  const resolvedParams = resolvedPairs.filter((pair) => pair !== '').join('&');
  if (!resolvedParams) {
    return resolvedLink;
  }

  // The query belongs BEFORE a link's #fragment (hash-routed SPAs).
  const hashIndex = resolvedLink.indexOf('#');
  const base =
    hashIndex === -1 ? resolvedLink : resolvedLink.slice(0, hashIndex);
  const fragment = hashIndex === -1 ? '' : resolvedLink.slice(hashIndex);
  return `${addBracketParameterToUrl(base, resolvedParams)}${fragment}`;
};
